import { useCallback, useRef, useState } from 'react';
import { streamAsk } from '@/lib/queries/ask';
import type { AskLang, AskSourceAyah } from '@/types/ai';

export type AskStatus = 'idle' | 'streaming' | 'done' | 'error';

export type AskState = {
  question: string;
  answer: string;
  sources: AskSourceAyah[];
  invalidCitations: string[];
  status: AskStatus;
  error: { code: string; message: string } | null;
};

const INITIAL: AskState = {
  question: '',
  answer: '',
  sources: [],
  invalidCitations: [],
  status: 'idle',
  error: null,
};

/**
 * Streaming Ask state. Single in-flight request at a time — calling
 * `ask()` aborts the previous one. `cancel()` aborts and resets to
 * `idle`. The hook is intentionally minimal — no React Query cache,
 * no history. Phase 6 is stateless single-shot.
 */
export function useAsk() {
  const [state, setState] = useState<AskState>(INITIAL);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState((prev) =>
      prev.status === 'streaming' ? { ...prev, status: 'idle' } : prev,
    );
  }, []);

  const ask = useCallback(async (question: string, lang: AskLang = 'en') => {
    // Cancel any in-flight stream.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState({
      question,
      answer: '',
      sources: [],
      invalidCitations: [],
      status: 'streaming',
      error: null,
    });

    try {
      for await (const evt of streamAsk({ question, lang }, controller.signal)) {
        if (controller.signal.aborted) break;
        switch (evt.type) {
          case 'token':
            setState((prev) => ({ ...prev, answer: prev.answer + evt.text }));
            break;
          case 'sources':
            setState((prev) => ({ ...prev, sources: evt.sources }));
            break;
          case 'verification':
            setState((prev) => ({
              ...prev,
              invalidCitations: evt.invalid_citations,
            }));
            break;
          case 'done':
            setState((prev) => ({ ...prev, status: 'done' }));
            break;
          case 'error':
            setState((prev) => ({
              ...prev,
              status: 'error',
              error: { code: evt.code, message: evt.message },
            }));
            break;
        }
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      const message = err instanceof Error ? err.message : String(err);
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: { code: 'client', message },
      }));
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState(INITIAL);
  }, []);

  return { ...state, ask, cancel, reset };
}
